import { Contact, FileText, Home, KeyRound, MessageSquareText, Network, Search, Users, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'wouter'
import { useI18n } from '../i18n'

type CommandPaletteProps = {
  open: boolean
  onClose: () => void
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { locale } = useI18n()
  const [, navigate] = useLocation()
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const zh = locale === 'zh-CN'
  const commands = useMemo(() => [
    { href: '/', label: zh ? '返回首页' : 'Go to homepage', hint: 'G H', icon: Home },
    { href: '/chat', label: zh ? '开始访谈' : 'Start an interview', hint: 'G I', icon: MessageSquareText },
    { href: '/resume', label: zh ? '查看履历' : 'Open resume', hint: 'G R', icon: FileText },
    { href: '/human-api', label: 'Human API', hint: 'G U', icon: Users },
    { href: '/graph', label: zh ? '浏览知识图谱' : 'Browse knowledge graph', hint: 'G K', icon: Network },
    { href: '/contact', label: zh ? '联系方式' : 'Contact', hint: 'G C', icon: Contact },
    { href: '/account', label: zh ? '账户与 API 密钥' : 'Account & API keys', hint: 'G A', icon: KeyRound },
  ], [zh])

  const filtered = commands.filter((command) => command.label.toLowerCase().includes(query.trim().toLowerCase()))

  useEffect(() => {
    if (!open) return
    setQuery('')
    const timer = window.setTimeout(() => inputRef.current?.focus(), 20)
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose, open])

  if (!open) return null

  const run = (href: string) => {
    navigate(href)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-110 flex items-start justify-center bg-ink/38 px-4 pt-[12vh] backdrop-blur-xs" role="dialog" aria-modal="true" aria-label={zh ? '命令面板' : 'Command palette'} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <div className="w-full max-w-xl overflow-hidden rounded-lg border border-ink/15 bg-white shadow-[0_28px_90px_rgba(16,47,80,.22)]">
        <label className="flex items-center gap-3 border-b border-ink/12 px-4">
          <Search size={17} className="text-moss" aria-hidden="true" />
          <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={zh ? '搜索页面或操作…' : 'Search pages and actions…'} className="h-14 min-w-0 flex-1 bg-transparent text-sm outline-hidden placeholder:text-ink/34" />
          <button type="button" onClick={onClose} className="grid size-8 place-items-center rounded-lg border border-ink/12 text-ink/55 transition hover:border-moss/45 hover:text-moss" aria-label={zh ? '关闭' : 'Close'}><X size={14} /></button>
        </label>
        <div className="max-h-[55vh] overflow-y-auto p-2">
          {filtered.map(({ href, label, hint, icon: Icon }) => (
            <button key={href} type="button" onClick={() => run(href)} className="group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition hover:bg-[#edf3f7] focus-visible:bg-[#edf3f7]">
              <span className="grid size-8 place-items-center rounded-lg border border-ink/10 bg-paper text-moss"><Icon size={14} /></span>
              <span className="min-w-0 flex-1 text-[12px] font-semibold text-ink">{label}</span>
              <kbd className="font-mono text-[9px] text-ink/36">{hint}</kbd>
            </button>
          ))}
          {!filtered.length && <p className="px-4 py-10 text-center text-xs text-ink/45">{zh ? '没有匹配的操作' : 'No matching command'}</p>}
        </div>
        <div className="flex items-center justify-between border-t border-ink/10 bg-paper px-4 py-2.5 text-[9px] text-ink/42">
          <span>{zh ? '点击执行 · Esc 关闭' : 'Click to run · Esc to close'}</span><span>jz-ai-chat.com</span>
        </div>
      </div>
    </div>
  )
}
